import { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import {
    Utensils,
    Download,
    X,
    Plus,
    GripVertical,
    Tag,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { downloadDailyMenuPdf } from "../lib/dailyMenuPdf";
import { useAuth } from "../lib/auth";
import BrandWizardDialog from "./BrandWizardDialog";

const ALLERGENS_PRESET = [
    "Glutine",
    "Lattosio",
    "Uova",
    "Pesce",
    "Crostacei",
    "Molluschi",
    "Frutta a guscio",
    "Soia",
    "Arachidi",
    "Sedano",
    "Senape",
    "Sesamo",
    "Solfiti",
    "Lupini",
];

const TITLE_PRESETS = [
    "Piatti del Giorno",
    "Lo Chef Consiglia",
    "Menu del Giorno",
    "Specialità della Casa",
    "Suggerimenti dello Chef",
];

/**
 * Wizard "Crea Piatti del Giorno": parte da un Magic Menu (i piatti
 * generati dall'AI), permette all'utente di selezionare i piatti, editare
 * nome/allergeni/prezzo, aggiungere un messaggio (es. "acqua compresa") e
 * scaricare il PDF A4 pronto per la sala.
 */
export default function DailyMenuBuilderDialog({
    open,
    onOpenChange,
    magicMenu,
}) {
    const { user } = useAuth();
    const [title, setTitle] = useState("Piatti del Giorno");
    const [restaurantName, setRestaurantName] = useState("");
    const [message, setMessage] = useState("");
    const [dishes, setDishes] = useState([]);
    const [saving, setSaving] = useState(false);
    const [brandOpen, setBrandOpen] = useState(false);
    const [savedMenu, setSavedMenu] = useState(null);

    useEffect(() => {
        if (!open || !magicMenu) return;
        setRestaurantName(user?.restaurant_name || "");
        setTitle("Piatti del Giorno");
        setMessage("");
        // Pre-seleziona tutti i piatti del magic menu come default
        const seed = (magicMenu.dishes || []).map((d) => ({
            included: true,
            name: d.name || "",
            description: d.description || "",
            allergens: Array.isArray(d.allergens) ? [...d.allergens] : [],
            price_eur:
                d.suggested_price_eur != null && d.suggested_price_eur > 0
                    ? Number(d.suggested_price_eur)
                    : suggestPrice(d),
            allergens_input: "",
        }));
        setDishes(seed);
    }, [open, magicMenu, user]);

    const selectedDishes = useMemo(
        () => dishes.filter((d) => d.included && d.name.trim()),
        [dishes],
    );

    const updateDish = (i, patch) =>
        setDishes((p) => p.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));

    const addEmptyDish = () =>
        setDishes((p) => [
            ...p,
            {
                included: true,
                name: "",
                description: "",
                allergens: [],
                price_eur: 0,
                allergens_input: "",
            },
        ]);

    const removeDish = (i) =>
        setDishes((p) => p.filter((_, idx) => idx !== i));

    const toggleAllergen = (i, a) =>
        setDishes((p) =>
            p.map((d, idx) => {
                if (idx !== i) return d;
                const set = new Set(d.allergens);
                if (set.has(a)) set.delete(a);
                else set.add(a);
                return { ...d, allergens: Array.from(set) };
            }),
        );

    const generatePdf = async () => {
        if (selectedDishes.length === 0) {
            toast.error("Aggiungi almeno un piatto al menu");
            return;
        }
        setSaving(true);
        try {
            // Salva su backend per averlo nello storico
            const payload = {
                title: title.trim() || "Piatti del Giorno",
                restaurant_name: restaurantName.trim(),
                message: message.trim(),
                source_magic_menu_id: magicMenu?.id || null,
                dishes: selectedDishes.map((d) => ({
                    name: d.name.trim(),
                    description: d.description.trim(),
                    allergens: d.allergens,
                    price_eur: Number(d.price_eur) || null,
                })),
            };
            const { data } = await api.post("/daily-menus", payload);
            setSavedMenu(data);
            // Apri il wizard brand (logo/colore/stile) prima del download
            setBrandOpen(true);
            toast.success("Menu salvato. Personalizza il PDF e scaricalo.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const downloadWithBrand = (brand) => {
        if (!savedMenu) return;
        downloadDailyMenuPdf(savedMenu, brand);
        onOpenChange(false);
    };

    return (
        <>
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl bg-white p-0 overflow-hidden max-h-[92vh] flex flex-col"
                data-testid="daily-menu-dialog"
            >
                <DialogHeader className="px-6 pt-5 pb-4 border-b border-brand-line bg-gradient-to-r from-brand-gold/15 to-paper">
                    <div className="flex items-start gap-3">
                        <div className="h-11 w-11 rounded-full bg-brand-navy text-brand-gold flex items-center justify-center shrink-0">
                            <Utensils size={20} />
                        </div>
                        <div className="min-w-0">
                            <DialogTitle className="font-display text-2xl text-brand-ink leading-tight">
                                Crea i Piatti del Giorno
                            </DialogTitle>
                            <DialogDescription className="text-sm text-brand-muted mt-1">
                                Personalizza il menu, scegli quali piatti includere, modifica
                                allergeni e prezzo, scarica il PDF A4 per la sala.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {/* Titolo + nome ristorante */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <div className="eyebrow text-brand-ink/70 mb-1.5">Titolo del menu</div>
                            <input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                list="title-presets"
                                placeholder="Es. Piatti del Giorno, Lo Chef Consiglia…"
                                data-testid="daily-title"
                                className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                            />
                            <datalist id="title-presets">
                                {TITLE_PRESETS.map((t) => <option key={t} value={t} />)}
                            </datalist>
                        </div>
                        <div>
                            <div className="eyebrow text-brand-ink/70 mb-1.5">Nome ristorante</div>
                            <input
                                value={restaurantName}
                                onChange={(e) => setRestaurantName(e.target.value)}
                                placeholder="Comparirà sul PDF"
                                data-testid="daily-restaurant-name"
                                className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                            />
                        </div>
                    </div>

                    {/* Messaggio */}
                    <div>
                        <div className="eyebrow text-brand-ink/70 mb-1.5">Messaggio per i clienti (opzionale)</div>
                        <textarea
                            rows={2}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Es. Con i piatti del giorno l'acqua è compresa · Il pane è in omaggio · Buon appetito!"
                            data-testid="daily-message"
                            className="w-full px-3 py-2 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold resize-none"
                        />
                    </div>

                    {/* Lista piatti */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <div className="eyebrow text-brand-ink/70">Piatti del menu</div>
                            <span className="text-[11px] text-brand-muted">
                                {selectedDishes.length} su {dishes.length} inclusi
                            </span>
                        </div>
                        <div className="space-y-3" data-testid="daily-dishes">
                            {dishes.map((d, i) => (
                                <DailyDishRow
                                    key={i}
                                    dish={d}
                                    index={i}
                                    onUpdate={(patch) => updateDish(i, patch)}
                                    onToggleAllergen={(a) => toggleAllergen(i, a)}
                                    onRemove={() => removeDish(i)}
                                />
                            ))}
                            <button
                                type="button"
                                onClick={addEmptyDish}
                                data-testid="daily-add-dish"
                                className="w-full rounded-lg border border-dashed border-brand-line py-3 text-[12px] text-brand-muted hover:border-brand-gold hover:text-brand-navy transition inline-flex items-center justify-center gap-1.5"
                            >
                                <Plus size={13} /> Aggiungi piatto manualmente
                            </button>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[11px] text-brand-muted">
                        Il menu viene salvato nello storico e scaricato come PDF A4
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                        >
                            Annulla
                        </button>
                        <button
                            type="button"
                            onClick={generatePdf}
                            disabled={saving || selectedDishes.length === 0}
                            data-testid="daily-generate-pdf"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition disabled:opacity-50 shadow"
                        >
                            <Download size={14} />
                            {saving ? "Salvataggio…" : "Personalizza & Scarica"}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
        <BrandWizardDialog
            open={brandOpen}
            onOpenChange={setBrandOpen}
            onConfirm={downloadWithBrand}
            title="Stile del PDF Piatti del Giorno"
            confirmLabel="Scarica PDF"
        />
        </>
    );
}

function DailyDishRow({ dish, index, onUpdate, onToggleAllergen, onRemove }) {
    return (
        <div
            className={`rounded-xl border p-3 transition ${
                dish.included ? "border-brand-line bg-paper" : "border-brand-line/50 bg-brand-cream/40 opacity-70"
            }`}
            data-testid={`daily-dish-row-${index}`}
        >
            <div className="flex items-start gap-2">
                <input
                    type="checkbox"
                    checked={dish.included}
                    onChange={(e) => onUpdate({ included: e.target.checked })}
                    data-testid={`daily-include-${index}`}
                    title={dish.included ? "Escludi dal menu" : "Includi nel menu"}
                    className="mt-2.5 h-4 w-4 accent-brand-navy shrink-0"
                />
                <GripVertical size={14} className="mt-3 text-brand-muted shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2">
                        <input
                            value={dish.name}
                            onChange={(e) => onUpdate({ name: e.target.value })}
                            placeholder="Nome piatto"
                            data-testid={`daily-name-${index}`}
                            className="flex-1 px-3 py-2 rounded-lg bg-white border border-brand-line text-sm font-semibold focus:outline-none focus:border-brand-gold"
                        />
                        <div className="relative shrink-0">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-brand-muted">€</span>
                            <input
                                type="number"
                                step="0.5"
                                min="0"
                                value={dish.price_eur}
                                onChange={(e) => onUpdate({ price_eur: e.target.value })}
                                data-testid={`daily-price-${index}`}
                                className="w-24 pl-7 pr-3 py-2 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={onRemove}
                            className="h-8 w-8 rounded-full text-brand-muted hover:text-brand-danger hover:bg-red-50 flex items-center justify-center shrink-0 transition"
                            data-testid={`daily-remove-${index}`}
                            title="Rimuovi"
                        >
                            <X size={13} />
                        </button>
                    </div>
                    <textarea
                        rows={1}
                        value={dish.description}
                        onChange={(e) => onUpdate({ description: e.target.value })}
                        placeholder="Descrizione (opzionale)"
                        data-testid={`daily-desc-${index}`}
                        className="w-full px-3 py-1.5 rounded-lg bg-white border border-brand-line text-[12px] focus:outline-none focus:border-brand-gold resize-none"
                    />
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-brand-muted mb-1 inline-flex items-center gap-1">
                            <Tag size={9} /> Allergeni
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {ALLERGENS_PRESET.map((a) => {
                                const active = dish.allergens.includes(a);
                                return (
                                    <button
                                        key={a}
                                        type="button"
                                        onClick={() => onToggleAllergen(a)}
                                        data-testid={`daily-allergen-${index}-${a}`}
                                        className={`text-[10px] px-2 py-0.5 rounded-full border transition ${
                                            active
                                                ? "bg-brand-navy text-white border-brand-navy font-bold"
                                                : "bg-white text-brand-muted border-brand-line hover:border-brand-gold/60"
                                        }`}
                                    >
                                        {a}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Stima un prezzo di vendita "di emergenza" se l'AI non l'ha fornito */
function suggestPrice(dish) {
    // Heuristic minimale: 8€ antipasto/contorno, 12€ primo, 16€ secondo, 6€ dessert
    const c = (dish.course || "").toLowerCase();
    if (c.includes("antipasto")) return 8;
    if (c.includes("primo")) return 12;
    if (c.includes("secondo")) return 16;
    if (c.includes("contorno")) return 5;
    if (c.includes("dessert")) return 6;
    return 10;
}
